<template>
  <div class="goal-v2">
    <div class="wrap">
      <div class="mast">
        <h1>국내영업본부 실적판</h1>
        <span class="org">{{ mastCaption }}</span>
        <select
          v-model="selectedYear"
          style="margin-left: auto"
          aria-label="연도"
          @change="load"
        >
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}년</option>
        </select>
      </div>

      <!-- 로딩 · 오류 상태를 빈 화면으로 두지 않는다 -->
      <div v-if="loading" class="state">
        <div class="skel" />
        <div class="skel" />
        불러오는 중입니다…
      </div>

      <div v-else-if="error" class="state">
        <div>실적 데이터를 불러오지 못했습니다.</div>
        <div style="font-size: 12px; margin-top: 6px">{{ error }}</div>
        <button type="button" class="retry" @click="load">다시 시도</button>
      </div>

      <template v-else-if="data">
        <!-- ================= 필터 ================= -->
        <section class="fbar" aria-label="필터">
          <div class="frow">
            <div class="fg">
              <label>기간</label>
              <div class="segs">
                <button
                  v-for="(p, i) in presets"
                  :key="i"
                  type="button"
                  :aria-pressed="String(filter.m0 === p[1] && filter.m1 === p[2])"
                  @click="applyPreset(p)"
                >
                  {{ p[0] }}
                </button>
              </div>
            </div>

            <div class="fg">
              <label>월 범위</label>
              <div class="mrange">
                <select v-model.number="filter.m0" aria-label="시작 월" @change="clampRange('m0')">
                  <option v-for="m in 12" :key="m" :value="m">{{ m }}</option>
                </select>
                <span>~</span>
                <select v-model.number="filter.m1" aria-label="종료 월" @change="clampRange('m1')">
                  <option v-for="m in 12" :key="m" :value="m">{{ m }}</option>
                </select>
                <span>월</span>
              </div>
            </div>

            <div class="fg">
              <label>지점</label>
              <select v-model.number="filter.dept" aria-label="지점">
                <option :value="-1">전체 지점</option>
                <option v-for="d in deptOptions" :key="d.idx" :value="d.idx">{{ d.name }}</option>
              </select>
            </div>

            <div class="fg">
              <label title="계약서 유형이 아니라 계약의 contractGroup 기준입니다">
                그룹 <span style="text-transform: none; letter-spacing: 0">(계약 기준)</span>
              </label>
              <div class="segs">
                <button
                  v-for="g in groupOptions"
                  :key="g.v"
                  type="button"
                  :aria-pressed="String(filter.group === g.v)"
                  @click="filter.group = g.v"
                >
                  {{ g.label }}
                </button>
              </div>
            </div>

            <div class="freset">
              <button type="button" @click="resetFilter">초기화</button>
            </div>
          </div>

          <div class="fsum">
            <template v-if="filterTags.length">
              <span v-for="t in filterTags" :key="t" class="tag">{{ t }}</span>
            </template>
            <span v-else>전체 기간 · 전체 지점 · 전체 그룹</span>

            <span class="gv2-badge"><i style="background: var(--c-done)" />완료</span>
            <span v-if="inclusion.S" class="gv2-badge"><i style="background: var(--c-sign)" />서명중</span>
            <span v-if="inclusion.E" class="gv2-badge"><i style="background: var(--c-exp)" />기한경과</span>
            <span>을 실적으로 계산 · 취소 제외</span>

            <details class="cfg" :open="cfgOpen" @toggle="cfgOpen = $event.target.open">
              <summary>기준 바꾸기</summary>
              <div class="toggles">
                <button
                  v-for="t in inclusionToggles"
                  :key="t.k"
                  type="button"
                  class="tg"
                  :data-k="t.k"
                  :aria-pressed="String(inclusion[t.k])"
                  :disabled="t.locked"
                  @click="toggleInclusion(t.k)"
                >
                  <span class="sw" />
                  <span><b>{{ t.label }}</b><span class="d">{{ t.desc }}</span></span>
                </button>
              </div>
            </details>
          </div>
        </section>

        <!-- ================= KPI ================= -->
        <section class="kpis">
          <button type="button" class="kpi" @click="openModal('amount')">
            <span class="lab">누적 실적</span>
            <div class="big num">{{ eok(included[1]) }}<em>억원</em></div>
            <div class="sub num">{{ won(included[0]) }}건</div>
            <div class="split">
              <span
                v-for="k in ['C', 'S', 'E']"
                :key="k"
                class="r"
                :style="inclusion[k] ? null : 'opacity:.45'"
              >
                <span>{{ inclusion[k] ? '' : '제외 · ' }}{{ statusLabel[k] }}</span>
                <span class="num">{{ eok(buckets[k][1]) }}억</span>
              </span>
            </div>
            <span class="more">상세보기 →</span>
          </button>

          <button type="button" class="kpi" :class="{ gap: goalRange === null }" @click="openModal('goal')">
            <span class="lab">목표 대비 · {{ filter.m0 }}~{{ filter.m1 }}월</span>
            <template v-if="goalRange === null">
              <div class="big">산출 불가</div>
              <div class="sub"><span class="miss">{{ selectedYear }} 목표 미등록</span></div>
              <div class="split">
                <span class="r"><span>등록 방식</span><span>연 1건 · 월 12칸</span></span>
                <span class="r"><span>현재 상태</span><span>12칸 미완성</span></span>
              </div>
            </template>
            <template v-else>
              <div class="big num">{{ pct(included[1], goalRange) }}<em>%</em></div>
              <div class="sub num">목표 {{ eok(goalRange) }}억</div>
              <div class="split">
                <span class="r"><span>미달</span><span class="num">{{ eok(Math.max(goalRange - included[1], 0)) }}억</span></span>
                <span class="r">
                  <span>기준</span>
                  <span>{{ scopeFiltered ? '목표는 전사값' : '전사' }}</span>
                </span>
              </div>
            </template>
            <span class="more">상세보기 →</span>
          </button>

          <button type="button" class="kpi" @click="openModal('amount')">
            <span class="lab">전년 동기 대비 · {{ yoyRangeLabel }}</span>
            <template v-if="yoyAvailable">
              <div class="big num" :class="yoyDelta >= 0 ? 'up' : 'dn'">
                {{ yoyDelta >= 0 ? '+' : '' }}{{ yoyDelta.toFixed(1) }}<em>%</em>
              </div>
              <div class="sub num">{{ eok(yoyCurrent) }}억 · 완료 기준</div>
              <div class="split">
                <span class="r"><span>{{ selectedYear - 1 }} 동기</span><span class="num">{{ eok(yoyPrev) }}억</span></span>
                <span class="r"><span>증감액</span><span class="num">{{ eok(yoyCurrent - yoyPrev) }}억</span></span>
              </div>
              <!-- 선택 구간과 실제 비교 구간이 다르면 그 사실을 적는다. 조용히 잘라내지 않는다. -->
              <div v-if="yoyTrimmed" class="sub" style="margin-top: 4px; font-size: 11px">
                진행 중·미도래 월 제외 (선택 {{ filter.m0 }}~{{ filter.m1 }}월)
              </div>
            </template>
            <template v-else-if="!comparableMonths.length">
              <div class="big" style="font-size: 20px; color: var(--muted)">비교 불가</div>
              <div class="sub">선택 구간에 완료된 달이 없습니다 — 진행 중인 달만으로는 전년과 견줄 수 없습니다</div>
              <div class="split">
                <span class="r"><span>선택 구간</span><span>{{ filter.m0 }}~{{ filter.m1 }}월</span></span>
              </div>
            </template>
            <template v-else>
              <div class="big" style="font-size: 20px; color: var(--muted)">전체 기준만</div>
              <div class="sub">전년 실적을 지점·그룹 단위로 보유하지 않습니다</div>
              <div class="split">
                <span class="r"><span>{{ selectedYear - 1 }} 동기 전사</span><span class="num">{{ eok(yoyPrev) }}억</span></span>
              </div>
            </template>
            <span class="more">상세보기 →</span>
          </button>

          <button type="button" class="kpi exp" @click="openModal('recov')">
            <span class="lab">회수 대상 · 기한 지난 서명중</span>
            <div class="big num">{{ eok(buckets.E[1]) }}<em>억원</em></div>
            <div class="sub num">{{ won(buckets.E[0]) }}건</div>
            <div class="split">
              <span class="r"><span>취소</span><span class="num">{{ eok(buckets.X[1]) }}억 · {{ won(buckets.X[0]) }}건</span></span>
              <span class="r"><span>실적 대비</span><span class="num">{{ pct(buckets.E[1], included[1]) }}%</span></span>
            </div>
            <span class="more">상세보기 →</span>
          </button>
        </section>

        <div class="inline-kpi">
          <span>완료 건당 평균 <b class="num">{{ won(avgCompletedManwon) }}만원</b></span>
          <span>선택 구간 계약 <b class="num">{{ won(totalContracts) }}건</b></span>
          <span>집계 지점 <b class="num">{{ won(data.depts.length) }}곳</b></span>
        </div>

        <!-- ================= 월별 추세 ================= -->
        <div class="panel">
          <div class="ph">
            <div>
              <h3>월별 실적 · 전년 비교</h3>
              <p class="cap">
                완료 기준
                <template v-if="currentMonth">
                  · {{ currentMonth }}월은 <b>{{ currentDay }}일 경과</b> 진행 중이라 빗금 처리
                </template>
                <template v-if="yoyAvailable"> · 회색은 {{ selectedYear - 1 }}년</template>
                <template v-else> · <b>전년 비교는 전체 기준에서만</b> 표시됩니다</template>
              </p>
            </div>
            <button type="button" class="linkish" @click="openModal('amount')">상세보기</button>
          </div>

          <div class="gcols">
            <span
              v-for="(v, i) in monthly"
              :key="i"
              class="gm"
              :class="{ dim: i + 1 < filter.m0 || i + 1 > filter.m1 }"
              :title="barTitle(i)"
            >
              <b v-if="yoyAvailable" class="p" :style="{ height: barH(data.prevYearMonthly[i]) }" />
              <b :class="i + 1 === currentMonth ? 'ip' : 'c'" :style="{ height: barH(v) }" />
            </span>
          </div>
          <div class="axis">
            <span
              v-for="i in 12"
              :key="i"
              :class="{ off: i < filter.m0 || i > filter.m1 }"
            >{{ i }}</span>
          </div>
          <div class="legend">
            <span v-if="yoyAvailable" class="li"><i style="background: var(--prior)" />{{ selectedYear - 1 }}</span>
            <span class="li"><i style="background: var(--c-done)" />{{ selectedYear }} 완료</span>
            <span v-if="currentMonth" class="li">
              <i style="background: repeating-linear-gradient(45deg,var(--c-done),var(--c-done) 3px,transparent 3px,transparent 6px); border: 1px solid var(--c-done)" />진행 중
            </span>
          </div>
        </div>

        <!-- ================= 지점별 (전폭) ================= -->
        <div class="panel">
          <div class="ph">
            <div>
              <h3>지점별 실적</h3>
              <p class="cap">행을 누르면 그 지점으로 필터가 걸립니다 · 지점명은 하이픈·공백을 지워 병합했습니다</p>
            </div>
            <button type="button" class="linkish" @click="openModal('dept')">전체 보기</button>
          </div>
          <div class="scroll">
            <table>
              <thead>
                <tr><th>지점</th><th class="r">건수</th><th class="r">실적</th><th class="r">회수 대상</th><th class="r">취소</th></tr>
              </thead>
              <tbody>
                <tr
                  v-for="r in deptTableTop"
                  :key="r.idx"
                  class="clickable"
                  :class="{ sel: filter.dept === r.idx }"
                  @click="toggleDept(r.idx)"
                >
                  <td>{{ r.name }}</td>
                  <td class="r num">{{ won(r.count) }}</td>
                  <td class="r num">{{ eok(r.amount) }}억</td>
                  <td class="r num" :style="{ color: r.expiredCount ? 'var(--c-exp)' : 'var(--muted)' }">
                    {{ r.expiredCount ? eok(r.expiredAmount) + '억 · ' + r.expiredCount + '건' : '—' }}
                  </td>
                  <td class="r num" :style="{ color: r.canceledCount ? 'var(--ink-2)' : 'var(--muted)' }">
                    {{ r.canceledCount ? eok(r.canceledAmount) + '억' : '—' }}
                  </td>
                </tr>
                <tr v-if="!deptTableTop.length">
                  <td colspan="5" class="empty">선택한 조건에 해당하는 계약이 없습니다.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ================= 매출 구성 (유형 + 품목) ================= -->
        <div class="panel">
          <div class="ph">
            <div>
              <h3>매출 구성 <span class="cav">계약서 유형 기준</span></h3>
              <p class="cap">기간 {{ filter.m0 }}~{{ filter.m1 }}월 · 그룹 {{ groupName }} · 지점 필터 미적용</p>
            </div>
            <button type="button" class="linkish" @click="openModal('mix')">상세보기</button>
          </div>

          <div class="bars">
            <div v-for="t in typeTable" :key="t.name" class="bar-row">
              <span class="nm" :title="t.name + ' (' + t.group + ')'">{{ t.name }}</span>
              <span class="track" :style="{ width: trackW(t.amount) }">
                <span
                  v-for="(s, si) in typeSegments(t)"
                  :key="si"
                  class="seg"
                  :style="{ flex: '0 0 ' + s.pct + '%', background: s.color }"
                />
              </span>
              <span class="vl num">{{ eok(t.amount) }}억</span>
            </div>
            <p v-if="!typeTable.length" class="empty">선택한 조건에 해당하는 계약이 없습니다.</p>
          </div>

          <div class="legend">
            <span v-for="l in activeStatusLegend" :key="l.k" class="li">
              <i :style="{ background: l.color }" />{{ l.label }}
            </span>
          </div>

          <!-- ---- 품목 기준 (신규) ---- -->
          <div class="subsection">
            <div class="ph">
              <div>
                <h4>품목 기준 <span class="cav">계약 항목별 금액</span></h4>
                <p class="cap">
                  기간 {{ filter.m0 }}~{{ filter.m1 }}월 · 지점 {{ deptName }} ·
                  <b>그룹 필터는 이 표에 적용되지 않습니다</b>
                </p>
              </div>
            </div>

            <!--
              [HARD] 데이터가 있으면 항상 보이는 고지. 품목 합계는 총매출이 아니다.
              집계가 0 이면 띄우지 않는다 — 비중을 0 으로 나누게 되어 "미분류 100%" 라는
              근거 없는 문장이 만들어진다. 그 경우는 아래 빈 상태 문구가 맡는다.
            -->
            <div v-if="items.total > 0" class="warn unc">
              <span>
                <b>품목 합계는 총매출이 아닙니다</b> — 이 구간 매출 {{ eok(items.total) }}억 중
                <b>품목 미분류 {{ won(items.unclassifiedCount) }}건 · {{ eok(items.unclassifiedAmount) }}억</b>이
                품목으로 쪼개지지 않습니다(전체의 {{ unclassifiedPct }}%).
                계약서 폼이 항목 네 칸을 필수로 받지 않아 작성자가 비운 계약이 남고, 항목별 저장 이전
                계약과 프릭스 계약도 미분류입니다.
              </span>
            </div>

            <div v-if="items.total > 0">
              <div class="stack">
                <span
                  v-for="r in items.rows"
                  :key="r.key"
                  class="seg"
                  :class="{ unc: r.key === 'unclassified' }"
                  :style="{
                    flex: '0 0 ' + (r.share * 100).toFixed(2) + '%',
                    background: r.key === 'unclassified' ? null : itemColor[r.key],
                  }"
                  :title="r.label + ' ' + eok(r.amount) + '억'"
                />
              </div>

              <div class="scroll" style="margin-top: 14px">
                <table>
                  <thead>
                    <tr><th>품목</th><th class="r">금액</th><th class="r">비중</th><th class="r">건수</th></tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in items.rows"
                      :key="r.key"
                      :class="{ 'unc-row': r.key === 'unclassified' }"
                    >
                      <td>
                        <i
                          class="swatch"
                          :style="{ background: r.key === 'unclassified' ? 'var(--c-unc)' : itemColor[r.key] }"
                        />{{ r.label }}
                      </td>
                      <td class="r num">{{ eok(r.amount) }}억</td>
                      <td class="r num">{{ (r.share * 100).toFixed(1) }}%</td>
                      <td class="r num">{{ r.key === 'unclassified' ? won(r.count) + '건' : '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p v-else class="empty">선택한 조건에 품목 금액이 집계된 계약이 없습니다.</p>
          </div>
        </div>

        <!-- ================= 데이터 품질 ================= -->
        <div class="note">
          <b>데이터 품질 — 읽기 전에 알아둘 것</b>
          <ul>
            <li>
              <span class="num">대구-1지점 / 대구1지점 / 대구 1지점</span> 처럼 하이픈·공백만 다른 표기가 섞여
              집계가 쪼개집니다. 이 화면은 <b>하이픈·공백을 지워 병합</b>했습니다.
            </li>
            <li>지점명 칸에 <b>사람 이름</b>이 들어갔거나 빈 값인 계약은 <span class="num">미분류</span> 지점으로 묶입니다.</li>
            <li><span class="num">영업기획팀</span> · <span class="num">강남센터</span> 등에 1원·11원 단위 계약이 있습니다 — 테스트 계약으로 보입니다.</li>
            <li>
              <b>품목별 금액은 2026-09-08부터 저장됩니다.</b> 그날 배포 이후 발행된
              <span class="num">물품매매V4</span> 계약은 임플란트·재료·장비·기타 항목 금액이 DB에 남아
              위 <b>품목 기준</b> 표로 집계됩니다. 다만 <b>그 이전 계약</b>, <b>작성자가 항목 칸을 비운 계약</b>,
              <b>프릭스로 발행된 계약</b>은 총액만 있어 <span class="num">품목 미분류</span>로 남습니다<template
                v-if="items.total > 0"> (현재 {{ unclassifiedPct }}%)</template>. 과거 계약의 항목 금액
              회수(백필)는 예정되어 있습니다.
            </li>
          </ul>
        </div>

        <div class="foot">
          <span class="pill">contracts-api · goals/v2/dashboard</span>
          <span class="pill">{{ selectedYear }}년 · {{ generatedAtText }}</span>
          <span class="pill">필터는 재조회 없이 큐브를 접습니다</span>
        </div>
      </template>
    </div>

    <!-- ================= 모달 ================= -->
    <div
      v-if="modal"
      class="goal-v2-ovl"
      @click.self="closeModal"
    >
      <div class="gv2-modal" role="dialog" aria-modal="true" aria-labelledby="goalv2-modal-title">
        <div class="mbar">
          <div>
            <h2 id="goalv2-modal-title">{{ modalMeta.title }}</h2>
            <div class="mcap">{{ modalMeta.caption }}</div>
          </div>
          <button ref="modalClose" type="button" class="xbtn" aria-label="닫기" @click="closeModal">✕</button>
        </div>

        <div class="mstats">
          <div v-for="(s, i) in modalMeta.stats" :key="i" class="st">
            <div class="l">{{ s[0] }}</div>
            <div class="v num">{{ s[1] }}</div>
          </div>
        </div>

        <div class="mbody">
          <!-- 누적 실적 -->
          <template v-if="modal === 'amount'">
            <section>
              <h4>상태 구성</h4>
              <div class="scroll">
                <table>
                  <thead><tr><th>상태</th><th>처리</th><th class="r">건수</th><th class="r">금액</th><th class="r">비중</th></tr></thead>
                  <tbody>
                    <tr
                      v-for="k in statusKeys"
                      :key="k"
                      :style="k !== 'X' && inclusion[k] ? null : 'color:#6b7269'"
                    >
                      <td>{{ statusLongLabel[k] }}</td>
                      <td>{{ k !== 'X' && inclusion[k] ? '산입' : '제외' }}</td>
                      <td class="r num">{{ won(buckets[k][0]) }}</td>
                      <td class="r num">{{ eok(buckets[k][1]) }}억</td>
                      <td class="r num">{{ pct(buckets[k][1], allStatusAmount) }}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
            <section>
              <h4>월별 완료 실적<template v-if="yoyAvailable"> · 전년 비교</template></h4>
              <div class="scroll">
                <table>
                  <thead>
                    <tr><th>월</th><th class="r">{{ selectedYear - 1 }}</th><th class="r">{{ selectedYear }}</th><th class="r">증감</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="m in rangeMonths" :key="m">
                      <td>{{ m }}월 <span v-if="m === currentMonth" class="chip d3">진행 중</span></td>
                      <td class="r num">{{ yoyAvailable ? eok(data.prevYearMonthly[m - 1]) + '억' : '—' }}</td>
                      <td class="r num">{{ eok(monthly[m - 1]) }}억</td>
                      <td class="r num">{{ monthDeltaText(m) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-if="!yoyAvailable" class="cap">
                전년 비교는 전체 기준으로만 제공됩니다 — 전년 실적을 지점·그룹 단위로 보유하고 있지 않습니다.
              </p>
            </section>
          </template>

          <!-- 목표 대비 -->
          <template v-else-if="modal === 'goal'">
            <section>
              <h4>월별 목표 대비 · {{ selectedYear }}년</h4>
              <div v-if="data.goals" class="scroll">
                <table>
                  <thead><tr><th>월</th><th class="r">목표</th><th class="r">완료 실적</th><th class="r">달성률</th></tr></thead>
                  <tbody>
                    <tr v-for="m in 12" :key="m">
                      <td>{{ m }}월 <span v-if="m === currentMonth" class="chip d3">진행 중</span></td>
                      <td class="r num">{{ eok(data.goals[m - 1]) }}억</td>
                      <td class="r num">{{ eok(monthly[m - 1]) }}억</td>
                      <td class="r num">{{ pct(monthly[m - 1], data.goals[m - 1]) }}%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="empty">
                {{ selectedYear }}년 목표가 등록되지 않았습니다. 목표는 12개월이 모두 채워져야 달성률이 산출됩니다 —
                일부만 등록되면 반쪽 목표로 계산돼 숫자가 거짓말을 하므로 산출하지 않습니다.
              </p>
              <p class="cap">
                목표는 전사 단위로만 등록되어 지점·그룹별 달성률은 산출할 수 없습니다.
                과거 연도 목표는 이 화면이 보유하지 않습니다.
              </p>
            </section>
          </template>

          <!-- 회수 대상 -->
          <template v-else-if="modal === 'recov'">
            <section>
              <h4>지점별 회수 대상 · {{ filter.m0 }}~{{ filter.m1 }}월</h4>
              <div class="scroll">
                <table>
                  <thead><tr><th>지점</th><th class="r">건수</th><th class="r">금액</th></tr></thead>
                  <tbody>
                    <tr v-for="r in recovRows" :key="r.idx">
                      <td>{{ r.name }}</td>
                      <td class="r num">{{ won(r.expiredCount) }}</td>
                      <td class="r num">{{ eok(r.expiredAmount) }}억</td>
                    </tr>
                    <tr v-if="!recovRows.length">
                      <td colspan="3" class="empty">이 조건에는 기한이 지난 서명중 계약이 없습니다.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p class="cap">
                서명 기한이 지났으나 취소되지 않은 계약입니다. 프로바이더는 만료를 상태로 주지 않아
                읽는 시점에 계산합니다. 계약 단건 목록(거래처·담당자·경과일)은 이 집계 API가 보유하지 않습니다.
              </p>
            </section>
          </template>

          <!-- 지점 목록 -->
          <template v-else-if="modal === 'dept'">
            <section>
              <h4>지점 전체 · {{ deptTable.length }}곳</h4>
              <div class="scroll">
                <table>
                  <thead>
                    <tr><th>지점</th><th class="r">건수</th><th class="r">실적</th><th class="r">회수 대상</th><th class="r">취소</th></tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="r in deptTable"
                      :key="r.idx"
                      class="clickable"
                      :class="{ sel: filter.dept === r.idx }"
                      @click="pickDept(r.idx)"
                    >
                      <td>{{ r.name }}</td>
                      <td class="r num">{{ won(r.count) }}</td>
                      <td class="r num">{{ eok(r.amount) }}억</td>
                      <td class="r num">{{ r.expiredCount ? eok(r.expiredAmount) + '억' : '—' }}</td>
                      <td class="r num">{{ r.canceledCount ? eok(r.canceledAmount) + '억' : '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </template>

          <!-- 매출 구성 상세 -->
          <template v-else-if="modal === 'mix'">
            <section>
              <h4>계약서 유형별</h4>
              <div class="scroll">
                <table>
                  <thead>
                    <tr><th>유형</th><th>그룹</th><th class="r">건수</th><th class="r">산입액</th><th class="r">완료</th><th class="r">기한경과</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in typeTable" :key="t.name">
                      <td>{{ t.name }}</td>
                      <td>{{ t.group }}</td>
                      <td class="r num">{{ won(t.count) }}</td>
                      <td class="r num">{{ eok(t.amount) }}억</td>
                      <td class="r num">{{ eok(t.completed) }}억</td>
                      <td class="r num">{{ t.expired ? eok(t.expired) + '억' : '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p class="cap">
                <b>이 표는 계약서 유형이지 품목이 아닙니다.</b> 2026-07-28 계약서 통합 이후 임플란트·재료·장비·기타는
                <span class="num">물품매매V4</span> 한 장 안의 금액 항목으로 들어가므로, 유형 축으로는 장비를 셀 수 없습니다.
                품목별 금액은 아래 표를 보십시오. 유형 축은 지점 정보를 함께 보유하지 않아 지점 필터도 적용되지 않습니다.
              </p>
            </section>

            <section>
              <h4>품목별</h4>
              <div class="scroll">
                <table>
                  <thead><tr><th>품목</th><th class="r">금액</th><th class="r">비중</th><th class="r">건수</th></tr></thead>
                  <tbody>
                    <tr v-for="r in items.rows" :key="r.key" :class="{ 'unc-row': r.key === 'unclassified' }">
                      <td>
                        <i
                          class="swatch"
                          :style="{ background: r.key === 'unclassified' ? '#7A5AA8' : itemColor[r.key] }"
                        />{{ r.label }}
                      </td>
                      <td class="r num">{{ eok(r.amount) }}억</td>
                      <td class="r num">{{ (r.share * 100).toFixed(1) }}%</td>
                      <td class="r num">{{ r.key === 'unclassified' ? won(r.count) + '건' : '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p class="cap">
                <b>품목 합계는 총매출이 아닙니다.</b> 품목 미분류 {{ won(items.unclassifiedCount) }}건 ·
                {{ eok(items.unclassifiedAmount) }}억이 빠져 있습니다. 품목 축에는 그룹 정보가 없어 그룹 필터가
                적용되지 않습니다.
              </p>
            </section>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import './goal-v2/goal-v2.scss';
</style>

<script>
import contractApi from '@/views/ContractManagement/api';
import {
  STATUS_KEYS,
  STATUS_LABEL,
  defaultFilter,
  defaultInclusion,
  aggregateByStatus,
  included as includedOf,
  monthlyCompleted,
  deptRows,
  deptOrderByCompleted,
  typeRows,
  itemBreakdown,
  goalInRange,
  prevYearInRange,
} from './goal-v2/cube';

/** 품목 축 색. 미분류는 SCSS 의 --c-unc 를 쓰므로 여기 없다. */
const ITEM_COLOR = {
  implant: '#2E7D4F',
  material: '#2C6FB5',
  equipment: '#C8850C',
  etc: '#8E9887',
};

const STATUS_COLOR = { C: '#2E7D4F', S: '#2C6FB5', E: '#C8850C' };

export default {
  name: 'GoalManagementV2',

  data() {
    const now = new Date();
    return {
      loading: false,
      error: null,
      data: null,
      selectedYear: now.getFullYear(),
      filter: defaultFilter(),
      inclusion: defaultInclusion(),
      cfgOpen: false,
      modal: null,
      lastFocused: null,
      itemColor: ITEM_COLOR,
      statusKeys: STATUS_KEYS,
      statusLabel: STATUS_LABEL,
      statusLongLabel: {
        C: '완료',
        S: '서명중 (기한 유효)',
        E: '서명중 (기한 경과)',
        X: '취소',
      },
      groupOptions: [
        { v: -1, label: '전체' },
        { v: 0, label: '영업' },
        { v: 1, label: '장비' },
      ],
      inclusionToggles: [
        { k: 'C', label: '완료', desc: '서명 완료', locked: true },
        { k: 'S', label: '서명중', desc: '기한 유효', locked: false },
        { k: 'E', label: '기한경과', desc: '회수 대상 · 기본 제외', locked: false },
      ],
    };
  },

  computed: {
    apiHandler() {
      return contractApi(this.$apiInstance, this.$store);
    },

    /** 연도 선택지. v1 이 2024/2025 로 고정돼 2026 을 못 보던 문제를 반복하지 않는다. */
    yearOptions() {
      const y = new Date().getFullYear();
      return [y, y - 1, y - 2, y - 3];
    },

    /** 서버가 준 기준 시각. 클라이언트 시계를 쓰지 않는다. */
    serverNow() {
      return this.data ? new Date(this.data.meta.now) : null;
    },
    /** 선택 연도가 서버 기준 연도와 같을 때만 "진행 중" 월이 존재한다. */
    currentMonth() {
      if (!this.serverNow) return null;
      return this.serverNow.getFullYear() === Number(this.selectedYear)
        ? this.serverNow.getMonth() + 1
        : null;
    },
    currentDay() {
      return this.serverNow ? this.serverNow.getDate() : null;
    },
    mastCaption() {
      if (!this.serverNow) return '';
      const d = this.serverNow;
      return `덴티움 · ${this.selectedYear}년 · ${d.getMonth() + 1}월 ${d.getDate()}일 기준`;
    },
    generatedAtText() {
      if (!this.data) return '';
      const d = new Date(this.data.meta.generatedAt);
      return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} 조회`;
    },

    presets() {
      const m = this.currentMonth;
      const base = [['전체', 1, 12], ['상반기', 1, 6], ['하반기', 7, 12]];
      if (!m) return base;
      return [...base, ['최근 3개월', Math.max(m - 2, 1), m], ['이번 달', m, m]];
    },

    deptOptions() {
      return this.data ? deptOrderByCompleted(this.data.cube, this.data.depts) : [];
    },

    buckets() {
      return this.data
        ? aggregateByStatus(this.data.cube, this.filter)
        : { C: [0, 0], S: [0, 0], E: [0, 0], X: [0, 0] };
    },
    included() {
      return includedOf(this.buckets, this.inclusion);
    },
    allStatusAmount() {
      return STATUS_KEYS.reduce((s, k) => s + this.buckets[k][1], 0);
    },
    totalContracts() {
      return STATUS_KEYS.reduce((s, k) => s + this.buckets[k][0], 0);
    },
    avgCompletedManwon() {
      const b = this.buckets.C;
      return b[0] ? Math.round(b[1] / b[0] / 10000) : 0;
    },

    monthly() {
      return this.data ? monthlyCompleted(this.data.cube, this.filter) : new Array(12).fill(0);
    },
    rangeMonths() {
      const out = [];
      for (let m = this.filter.m0; m <= this.filter.m1; m += 1) out.push(m);
      return out;
    },
    completedInRange() {
      return this.rangeMonths.reduce((s, m) => s + this.monthly[m - 1], 0);
    },
    prevRange() {
      return this.data ? prevYearInRange(this.data.prevYearMonthly, this.filter) : 0;
    },
    scopeFiltered() {
      return this.filter.dept >= 0 || this.filter.group >= 0;
    },

    /**
     * 전년과 **견줄 수 있는 달**만 고른다 — 선택 구간에서 진행 중인 달과 아직 오지 않은 달을 뺀다.
     *
     * [HARD] 이걸 안 하면 「전년 동기」가 동기가 아니게 된다. 기본 필터가 1~12월이라
     * 9월 8일에 보면 올해 9개월치를 전년 **12개월치**와 나누게 되고, 실제로는 거의 동률인 실적이
     * -31.9% 로 찍힌다(2026-09-08 운영 실측: 2026 1~8월 417.28억 vs 2025 1~8월 418.36억 = -0.3%,
     * 그런데 2025 연간 632.25억과 견주면 -31.9%). 진행 중인 달도 뺀다 — 8일치를 전년 한 달과
     * 견주는 것도 같은 종류의 거짓말이다.
     */
    comparableMonths() {
      const lastClosed = this.currentMonth ? this.currentMonth - 1 : 12;
      const end = Math.min(this.filter.m1, lastClosed);
      const out = [];
      for (let m = this.filter.m0; m <= end; m += 1) out.push(m);
      return out;
    },
    /** 비교 구간 라벨. 실제로 견준 달을 그대로 적는다 — 선택 구간과 다를 수 있음을 숨기지 않는다. */
    yoyRangeLabel() {
      const c = this.comparableMonths;
      if (!c.length) return '비교 가능 구간 없음';
      return `${c[0]}~${c[c.length - 1]}월`;
    },
    /** 선택 구간이 실제 비교 구간보다 넓은가 — 넓으면 화면이 그 사실을 밝힌다. */
    yoyTrimmed() {
      const c = this.comparableMonths;
      return c.length > 0 && c[c.length - 1] < this.filter.m1;
    },
    yoyCurrent() {
      return this.comparableMonths.reduce((s, m) => s + this.monthly[m - 1], 0);
    },
    yoyPrev() {
      if (!this.data) return 0;
      return this.comparableMonths.reduce((s, m) => s + (this.data.prevYearMonthly[m - 1] || 0), 0);
    },
    /**
     * 전년 실적은 전사 단위로만 보유한다 — 지점·그룹을 좁히면 비교가 성립하지 않는다.
     * 완료된 달이 하나도 없으면(1월 진행 중 등) 비교 자체가 없다.
     */
    yoyAvailable() {
      return !this.scopeFiltered && this.comparableMonths.length > 0 && this.yoyPrev > 0;
    },
    yoyDelta() {
      return this.yoyPrev ? (this.yoyCurrent / this.yoyPrev - 1) * 100 : 0;
    },

    goalRange() {
      return this.data ? goalInRange(this.data.goals, this.filter) : null;
    },

    deptTable() {
      return this.data ? deptRows(this.data.cube, this.data.depts, this.filter, this.inclusion) : [];
    },
    deptTableTop() {
      return this.deptTable.slice(0, 12);
    },
    recovRows() {
      return this.deptTable.filter((r) => r.expiredCount > 0).sort((a, b) => b.expiredAmount - a.expiredAmount);
    },

    typeTable() {
      return this.data ? typeRows(this.data.typeCube, this.filter, this.inclusion) : [];
    },
    typeMax() {
      return this.typeTable.length ? this.typeTable[0].amount : 1;
    },

    items() {
      return this.data
        ? itemBreakdown(this.data.itemCube, this.filter, this.inclusion)
        : { rows: [], totals: {}, classified: 0, unclassifiedAmount: 0, unclassifiedCount: 0, total: 0, coverage: 0 };
    },
    /**
     * 미분류 비중(%). 집계가 0 이면 `1 - coverage` 가 1 이 되어 "미분류 100%" 라는 근거 없는
     * 숫자가 나온다 — 호출부가 `items.total > 0` 일 때만 쓰지만 여기서도 막아둔다.
     */
    unclassifiedPct() {
      if (!this.items.total) return '0.0';
      return ((1 - this.items.coverage) * 100).toFixed(1);
    },

    barMax() {
      let mx = 0;
      for (let i = 0; i < 12; i += 1) {
        mx = Math.max(mx, this.monthly[i], this.yoyAvailable ? this.data.prevYearMonthly[i] : 0);
      }
      return mx || 1;
    },

    deptName() {
      return this.filter.dept < 0 ? '전체' : this.data.depts[this.filter.dept];
    },
    groupName() {
      return this.filter.group < 0 ? '전체' : ['영업', '장비'][this.filter.group];
    },
    filterTags() {
      const tags = [];
      if (!(this.filter.m0 === 1 && this.filter.m1 === 12)) tags.push(`${this.filter.m0}~${this.filter.m1}월`);
      if (this.filter.dept >= 0) tags.push(this.deptName);
      if (this.filter.group >= 0) tags.push(this.groupName);
      return tags;
    },

    activeStatusLegend() {
      return ['C', 'S', 'E']
        .filter((k) => this.inclusion[k])
        .map((k) => ({ k, label: STATUS_LABEL[k], color: STATUS_COLOR[k] }));
    },

    modalMeta() {
      const ctx = `기간 ${this.filter.m0}~${this.filter.m1}월 · 지점 ${this.deptName} · 그룹 ${this.groupName}`;
      const e = this.eok;
      switch (this.modal) {
        case 'amount':
          return {
            title: '누적 실적 상세',
            caption: ctx,
            stats: [
              ['산입 실적', `${e(this.included[1])}억`],
              ['산입 건수', this.won(this.included[0])],
              ['취소', `${e(this.buckets.X[1])}억`],
              ['회수 대상', `${e(this.buckets.E[1])}억`],
            ],
          };
        case 'goal':
          return {
            title: '목표 대비 상세',
            caption: `${this.selectedYear}년 · 전사 기준(지점·그룹 필터 미적용)`,
            stats: [
              ['연간 목표', this.data.goals ? `${e(this.data.goals.reduce((s, v) => s + v, 0))}억` : '미등록'],
              ['선택 구간 목표', this.goalRange === null ? '—' : `${e(this.goalRange)}억`],
              ['선택 구간 실적', `${e(this.included[1])}억`],
              ['달성률', this.goalRange === null ? '—' : `${this.pct(this.included[1], this.goalRange)}%`],
            ],
          };
        case 'recov':
          return {
            title: '회수 대상 상세',
            caption: `${ctx} · 서명 기한이 지났으나 취소되지 않은 계약`,
            stats: [
              ['대상 건수', this.won(this.buckets.E[0])],
              ['대상 금액', `${e(this.buckets.E[1])}억`],
              ['해당 지점', String(this.recovRows.length)],
              ['실적 대비', `${this.pct(this.buckets.E[1], this.included[1])}%`],
            ],
          };
        case 'dept':
          return {
            title: '지점별 실적',
            caption: `${ctx} · 행을 누르면 그 지점으로 필터가 걸립니다`,
            stats: [
              ['집계 지점', String(this.deptTable.length)],
              ['산입 실적', `${e(this.included[1])}억`],
              ['산입 건수', this.won(this.included[0])],
              ['회수 대상 지점', String(this.recovRows.length)],
            ],
          };
        case 'mix':
          return {
            title: '매출 구성 상세',
            caption: `기간 ${this.filter.m0}~${this.filter.m1}월 · 유형은 지점 필터 미적용 · 품목은 그룹 필터 미적용`,
            stats: [
              ['유형 수', String(this.typeTable.length)],
              ['품목 분해액', `${e(this.items.classified)}억`],
              ['품목 미분류', `${e(this.items.unclassifiedAmount)}억`],
              ['분해 비중', `${(this.items.coverage * 100).toFixed(1)}%`],
            ],
          };
        default:
          return { title: '', caption: '', stats: [] };
      }
    },
  },

  mounted() {
    this.load();
    document.addEventListener('keydown', this.onKeydown);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  },

  methods: {
    load() {
      this.loading = true;
      this.error = null;
      return this.apiHandler.dashboardV2(
        { year: this.selectedYear },
        (response) => {
          this.data = response.data;
          this.loading = false;
        },
        (err) => {
          this.error = (err && err.message) || '알 수 없는 오류';
          this.loading = false;
        },
      );
    },

    won(n) {
      return Number(n || 0).toLocaleString('ko-KR');
    },
    /** 억 단위 두 자리. 목업의 표기를 그대로 따른다. */
    eok(n) {
      return (Number(n || 0) / 100000000).toFixed(2);
    },
    /** 분모 0 을 항상 막는다 — 목표 미등록 구간에서 NaN/Infinity 가 새는 걸 방지. */
    pct(num, den) {
      return den ? ((num / den) * 100).toFixed(1) : '0.0';
    },

    barH(v) {
      return `${Math.max((v / this.barMax) * 100, 0.4).toFixed(1)}%`;
    },
    barTitle(i) {
      const m = i + 1;
      const cur = `${this.selectedYear} ${this.eok(this.monthly[i])}억`;
      const prev = this.yoyAvailable
        ? `${this.selectedYear - 1} ${this.eok(this.data.prevYearMonthly[i])}억 / `
        : '';
      return `${m}월 · ${prev}${cur}${m === this.currentMonth ? ' (진행 중)' : ''}`;
    },
    monthDeltaText(m) {
      if (!this.yoyAvailable) return '—';
      // 진행 중인 달은 며칠치를 전년 한 달과 견주게 되므로 증감을 내지 않는다.
      if (m === this.currentMonth) return '진행 중';
      const prev = this.data.prevYearMonthly[m - 1];
      if (!prev) return '—';
      return `${((this.monthly[m - 1] / prev - 1) * 100).toFixed(1)}%`;
    },

    trackW(amount) {
      return `${Math.max((amount / this.typeMax) * 100, 2).toFixed(1)}%`;
    },
    /** 유형 막대를 상태별로 나눈다. 산입 기준이 켜진 상태만 색 조각이 된다. */
    typeSegments(t) {
      const parts = [];
      if (this.inclusion.C && t.completed) parts.push({ v: t.completed, color: STATUS_COLOR.C });
      if (this.inclusion.E && t.expired) parts.push({ v: t.expired, color: STATUS_COLOR.E });
      const rest = t.amount - (this.inclusion.C ? t.completed : 0) - (this.inclusion.E ? t.expired : 0);
      if (this.inclusion.S && rest > 0) parts.push({ v: rest, color: STATUS_COLOR.S });
      return parts.map((p) => ({ ...p, pct: ((p.v / t.amount) * 100).toFixed(2) }));
    },

    applyPreset(p) {
      this.filter.m0 = p[1];
      this.filter.m1 = p[2];
    },
    /** 시작 월이 종료 월을 넘어서면 건드린 쪽을 기준으로 다른 쪽을 끌어온다. */
    clampRange(which) {
      if (this.filter.m0 <= this.filter.m1) return;
      if (which === 'm0') this.filter.m1 = this.filter.m0;
      else this.filter.m0 = this.filter.m1;
    },
    resetFilter() {
      this.filter = defaultFilter();
      this.inclusion = defaultInclusion();
    },
    toggleInclusion(k) {
      this.inclusion = { ...this.inclusion, [k]: !this.inclusion[k] };
      this.cfgOpen = true;
    },
    toggleDept(idx) {
      this.filter.dept = this.filter.dept === idx ? -1 : idx;
    },
    pickDept(idx) {
      this.filter.dept = idx;
      this.closeModal();
    },

    openModal(name) {
      this.lastFocused = document.activeElement;
      this.modal = name;
      document.body.style.overflow = 'hidden';
      this.$nextTick(() => {
        if (this.$refs.modalClose) this.$refs.modalClose.focus();
      });
    },
    closeModal() {
      this.modal = null;
      document.body.style.overflow = '';
      if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
      this.lastFocused = null;
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.modal) this.closeModal();
    },
  },
};
</script>
